/**
 * Product colors used across Bullrun surfaces.
 * Warm palette + chrome values are aligned with bullrun-mobile usage and web `BULLRUN_COLORS`.
 */
export const BULLRUN_COLORS = {
    black: "#110E0A",
    white: "#FFF8E8",
    gray: "#857A66",
    bgArea: "#15120D",
    bgWallet: "#1B1710",
    bgTabBar: "#211B12",
    bgRow: "#241E14",
    bgButton: "#2A2217",
    bgNavigation: "#110E0A",
    cardBackground: "#212028",
    background: "#0a0a0a",
    bgPanel: "#17130E",
    bgChartPanel: "#201A12",
    bgHoverButton: "#3B2F1D",
    bgHoverButtonDisabled: "#332A1C",
    borderSecondary: "#2E2619",
    borderPrimary: "#3E3322",
    borderAccent: "#8B6B1F",
    textPrimary: "#FFF8E8",
    textSecondary: "#B7A98C",
    textTertiary: "#857A66",
    accentPrimary: "#F7C948",
    accentPrimaryMuted: "#8B6B1F",
    accentPrimaryLight: "#FFE08A",
    accentSuccess: "#E6C46A",
    accentSuccessMuted: "#7D6A34",
    accentWarning: "#F7C948",
    accentAlert: "#FF6B6B",
    accentAlertSelected: "#C04848",
    accentAlertMuted: "#8C4C4C",
    /** Hot Markets / V01 dense UI — price change up/down (not gold `accentSuccess`). */
    changeUp: "#2dd4a4",
    changeDown: "#ef6767",
    changeFlat: "#8b8b98",
    /** V01 dense row primary copy (market names, wallet handles, prices). */
    denseTextPrimary: "#e8e8ec",
    /** V01 dense row muted copy (type labels, trade counts, timestamps). */
    denseTextMuted: "#8b8b98",
    changeUpMuted: "rgba(45, 212, 164, 0.12)",
    changeDownMuted: "rgba(239, 103, 103, 0.12)",
    changeUpMutedStrong: "rgba(45, 212, 164, 0.15)",
    changeUpMutedHover: "rgba(45, 212, 164, 0.2)",
    changeDownMutedHover: "rgba(239, 103, 103, 0.2)",
    /**
     * Named alpha constants for `accentPrimary` (#F7C948) used on web surfaces
     * (onboarding, glass chips). Values are fixed — update manually if the base
     * accent colour ever changes.
     */
    accentPrimaryA6: "rgba(247, 201, 72, 0.06)",
    accentPrimaryA8: "rgba(247, 201, 72, 0.08)",
    accentPrimaryA10: "rgba(247, 201, 72, 0.10)",
    accentPrimaryA14: "rgba(247, 201, 72, 0.14)",
    accentPrimaryA16: "rgba(247, 201, 72, 0.16)",
    accentPrimaryA55: "rgba(247, 201, 72, 0.55)",
    whiteA3: "rgba(255, 255, 255, 0.03)",
    whiteA6: "rgba(255, 255, 255, 0.06)",
    whiteA38: "rgba(255, 255, 255, 0.38)",
    /** Inactive pager dot — cream `textPrimary` at low alpha */
    textPrimaryA22: "rgba(255, 248, 232, 0.22)",
};
/**
 * Chrome copied from `apps/bullrun-mobile/src/navigation/MainNavigator.tsx`
 * (bottom tab bar + portfolio pill). Use these on web mobile shell for parity.
 */
export const BULLRUN_MOBILE_CHROME = {
    tabBarBackground: "#000000",
    portfolioPillHeightPx: 36,
    portfolioPillRadiusPx: 12,
    portfolioPillPaddingYPx: 6,
    portfolioPillPaddingXPx: 10,
    portfolioPillBackground: "#0A0A0A",
    /** RN uses `#FFFFFF40`; rgba is equivalent for web/CSS. */
    portfolioPillBorder: "rgba(255, 255, 255, 0.25)",
    portfolioPillBorderRn: "#FFFFFF40",
    portfolioPillSelectedBackground: "#FFFFFF",
    portfolioPillTextDefault: "#FFFFFF",
    portfolioPillTextSelected: "#000000",
    tabIconActive: "#FFFFFF",
    /** RN `#FFFFFF66` */
    tabIconInactive: "rgba(255, 255, 255, 0.4)",
};
/**
 * Home feed surfaces on bullrun-mobile (`HomeV2`, `NavigationTabs`, `HotMarketsSectionV2`,
 * `MultipleMarketsDisplay`, `AssetListItem`). Use on mobile-web for pixel-true parity.
 */
export const BULLRUN_MOBILE_HOME = {
    canvas: "#000000",
    /** `HomeV2` `paddingHorizontal` for the scroll content. */
    horizontalPaddingPx: 8,
    searchBarBorder: "#FFFFFF33",
    searchPlaceholder: "#FFFFFF99",
    searchBarInnerHeightPx: 48,
    searchBarRadiusPx: 12,
    tabInactive: "#666666",
    tabActive: "#FFFFFF",
    tabUnderline: "#FFFFFF",
    hotCardMetricsBackground: "#FFFFFF1A",
    predictionStripBackground: "#000000CC",
    predictionStripMutedLabel: "#FFFFFFCC",
    outcomeYesBackground: "#00C80533",
    outcomeNoBackground: "#EB5D2A33",
    outcomeButtonLabel: "#FFFFFF99",
    listDivider: "#FFFFFF33",
    listSecondaryLabel: "#666666",
    chartLinePositive: "#00C805",
    chartLineNegative: "#EB5D2A",
    /**
     * `MarketFilterTabs` on bullrun-mobile HomeV2 (Tokens / Perps / Predictions + watchlist filters).
     */
    marketFilterChipInactiveBg: "#1a1a1a",
    marketFilterChipActiveBg: "#2a2a2a",
    marketFilterChipBorder: "#FFFFFF33",
    marketFilterChipBorderActive: "transparent",
    marketFilterChipLabelInactive: "#FFFFFF99",
    marketFilterChipLabelActive: "#ffffff",
    marketFilterChipHeightPx: 36,
    marketFilterChipRadiusPx: 12,
    /** `AssetListItem` perp leverage pill */
    perpLeverageBadgeBg: "#12033A",
    perpLeverageBadgeBorder: "#9559F6",
    perpLeverageBadgeText: "#9559F6",
};
